import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Truck, Wrench } from "lucide-react";

export default function Onboarding() {
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("client");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("FR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/select-role", { role, phone, company, city, country });
      setUser(data);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="onboarding-page">
      <Header />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-md p-8">
          <div className="text-[11px] uppercase tracking-widest font-bold text-orange-600 mb-2">Finaliser votre profil</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight">Choisissez votre rôle</h1>
          <p className="text-sm text-slate-600 mt-1">Vous pouvez publier des demandes ou proposer vos services.</p>

          <div className="mt-6 grid grid-cols-2 gap-3" data-testid="onboarding-role-toggle">
            <button type="button" onClick={() => setRole("client")} className={`p-5 rounded-md border-2 text-left transition-all ${role === "client" ? "border-orange-600 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <Truck className={`h-5 w-5 ${role === "client" ? "text-orange-600" : "text-slate-600"}`} />
              <div className="font-display font-bold mt-2 text-slate-900">Client</div>
              <div className="text-xs text-slate-600 mt-1">Je veux publier une demande de dépannage</div>
            </button>
            <button type="button" onClick={() => setRole("pro")} className={`p-5 rounded-md border-2 text-left transition-all ${role === "pro" ? "border-orange-600 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <Wrench className={`h-5 w-5 ${role === "pro" ? "text-orange-600" : "text-slate-600"}`} />
              <div className="font-display font-bold mt-2 text-slate-900">Dépanneur</div>
              <div className="text-xs text-slate-600 mt-1">Je veux accéder au marché des missions</div>
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33…" />
              </div>
              <div>
                <Label>Ville</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Paris" />
              </div>
            </div>
            {role === "pro" && (
              <div>
                <Label>Société</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="SAS Dépannage…" />
              </div>
            )}
            <div>
              <Label>Pays</Label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="FR">🇫🇷 France</option>
                <option value="BE">🇧🇪 Belgique</option>
                <option value="ES">🇪🇸 Espagne</option>
                <option value="DE">🇩🇪 Allemagne</option>
              </select>
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="onboarding-submit">
              {loading ? "Enregistrement…" : "Valider mon profil"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
